import React from "react";
import { Row, Col, Typography, Statistic } from "antd";
import {
  RocketOutlined,
  SyncOutlined,
  TeamOutlined,
  StarOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined
} from '@ant-design/icons';
import styled from "styled-components";

const { Title, Text } = Typography;

// Main Container
const WhyContainer = styled.div`
  background: none;
  padding: 20px 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px 0;
  }
`;

// Hero Stats Section
const HeroStatsSection = styled.div`
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(239, 199, 60, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 220px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    min-width: 250px;
    height: 200px;
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    min-width: 100%;
    height: 180px;
    padding: 25px 15px;
  }
`;

// Features Grid Container
const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// Feature Block
const FeatureBlock = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  padding: 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border-left: 4px solid ${props => props.accentColor || '#6841eb'};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  }

  ${props => props.reverse && `
    flex-direction: row-reverse;
    border-left: none;
    border-right: 4px solid ${props.accentColor || '#6841eb'};
  `}

  @media (max-width: 768px) {
    flex-direction: column !important;
    text-align: center;
    padding: 30px 20px;
    border-left: none !important;
    border-right: none !important;
    border-top: 4px solid ${props => props.accentColor || '#6841eb'};
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.bgColor || '#6841eb'} 0%, ${props => props.bgColorSecondary || '#8b5cf6'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.reverse ? '0' : '40px'};
  margin-left: ${props => props.reverse ? '40px' : '0'};
  flex-shrink: 0;

  .anticon {
    font-size: 36px;
    color: white;
  }

  @media (max-width: 768px) {
    margin: 0 0 20px 0 !important;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled(Title)`
  color: #1a1a1a !important;
  margin-bottom: 12px !important;
  font-weight: 700 !important;
  font-size: 1.9rem !important;

  @media (max-width: 768px) {
    font-size: 1.3rem !important;
  }
`;

const FeatureDescription = styled(Text)`
  color: #666 !important;
  font-size: 22px !important;
  line-height: 1.6 !important;
  display: block;
`;

const WhyChronoflow = () => {
  const mainFeatures = [
    {
      icon: <RocketOutlined />,
      title: "A Talent Pool That Builds Itself",
      description: "Every candidate who applies or gets invited lands in your talent pool automatically — no manual entry. When someone updates their availability or job search status, you hear about it straight away, even when you're not in the platform.",
      accentColor: "#6841eb",
      bgColor: "#6841eb",
      bgColorSecondary: "#8b5cf6"
    },
    {
      icon: <SyncOutlined />,
      title: "AI That Works Before You Even Open the App",
      description: "Every application is screened and ranked against your criteria the moment it comes in — before you even open the app. You log in to a ranked shortlist, not a pile of CVs to sort through.",
      accentColor: "#10b981",
      bgColor: "#10b981",
      bgColorSecondary: "#34d399",
      reverse: true
    },
    {
      icon: <TeamOutlined />,
      title: "Recruiter and Hiring Manager, Finally in Sync",
      description: "Submit candidates to your hiring manager in one click — choosing exactly what they see. Managers get AI-generated summaries, built-in messaging, and a clear workspace. No email threads, no 'did you get my attachment', no Slack chasing. Decisions happen faster because everyone works in the same place.",
      accentColor: "#f59e0b",
      bgColor: "#f59e0b",
      bgColorSecondary: "#fbbf24"
    },
    {
      icon: <StarOutlined />,
      title: "Candidates Who Actually Know Where They Stand",
      description: "Every applicant gets live status updates as they move through your pipeline. Rejected candidates receive AI-generated feedback grounded in their actual interview notes — not a generic 'we'll keep your CV on file.' A better candidate experience means a better reputation for your brand.",
      accentColor: "#ef4444",
      bgColor: "#ef4444",
      bgColorSecondary: "#f87171",
      reverse: true
    },
    {
      icon: <CheckCircleOutlined />,
      title: "A Talent Sourcing Engine, Not a Subscription",
      description: "LinkedIn seats, job board credits, and paid candidate searches all reset to zero the moment you stop paying. Your ChronoFlow pool doesn't — every hire adds to something permanent that gets more valuable over time, not more expensive.",
      accentColor: "#8b5cf6",
      bgColor: "#8b5cf6",
      bgColorSecondary: "#a78bfa"
    }
  ];

  return (
    <WhyContainer>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Section Title */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <Title level={2} style={{ 
            color: "#000000", 
            marginBottom: "30px", 
            fontSize: "3.0rem", 
            fontWeight: "800"
          }} className="why-chronoflow-title">
            Why Chronoflow?
          </Title>
          <Text style={{
            fontSize: '26px',
            color: '#666',
            maxWidth: '800px',
            display: 'block',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Here's what actually happens behind the scenes, every time someone applies.
          </Text>
        </div>

        {/* Hero Stats */}
        <HeroStatsSection>
          <Row gutter={[32, 32]} justify="center">
            <Col xs={24} sm={8}>
              <StatCard>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                    <DollarCircleOutlined style={{ fontSize: '40px', color: '#f59e0b', marginRight: '12px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{
                        fontSize: '48px',
                        fontWeight: '800',
                        color: '#1a1a1a',
                        lineHeight: '1',
                        marginBottom: '4px'
                      }}>
                        70%
                      </div>
                    </div>
                  </div>
                  <div style={{ color: '#666', fontSize: '16px', fontWeight: '600' }}>
                    Less Than Your Current Stack
                  </div>
                </div>
                <Text style={{ color: '#666', fontSize: '18px', textAlign: 'center', display: 'block' }}>
                  Than running LinkedIn Recruiter, a job board, and a scheduling tool separately
                </Text>
              </StatCard>
            </Col>
            <Col xs={24} sm={8}>
              <StatCard>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                    <RocketOutlined style={{ fontSize: '40px', color: '#10b981', marginRight: '12px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ 
                        fontSize: '48px', 
                        fontWeight: '800', 
                        color: '#1a1a1a',
                        lineHeight: '1',
                        marginBottom: '4px'
                      }}>
                        2-in-1
                      </div>
                    </div>
                  </div>
                  <div style={{ color: '#666', fontSize: '16px', fontWeight: '600' }}>
                    Outbound Sourcing + Job Board
                  </div>
                </div>
                <Text style={{ color: '#666', fontSize: '16px', textAlign: 'center', display: 'block' }}>
                  Build your pool proactively, or let candidates come to you
                </Text>
              </StatCard>
            </Col>
            <Col xs={24} sm={8}>
              <StatCard>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                    <CheckCircleOutlined style={{ fontSize: '40px', color: '#6841eb', marginRight: '12px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ 
                        fontSize: '48px', 
                        fontWeight: '800', 
                        color: '#1a1a1a',
                        lineHeight: '1',
                        marginBottom: '4px'
                      }}>
                        0%
                      </div>
                    </div>
                  </div>
                  <div style={{ color: '#666', fontSize: '16px', fontWeight: '600' }}>
                    Manual Updates
                  </div>
                </div>
                <Text style={{ color: '#666', fontSize: '16px', textAlign: 'center', display: 'block' }}>
                  Your lean team focuses on recruiting, not data entry
                </Text>
              </StatCard>
            </Col>
          </Row>
        </HeroStatsSection>

        {/* Feature Blocks */}
        <FeaturesGrid>
          {mainFeatures.map((feature, index) => (
            <FeatureBlock 
              key={index} 
              reverse={feature.reverse}
              accentColor={feature.accentColor}
            >
              <IconWrapper 
                reverse={feature.reverse}
                bgColor={feature.bgColor}
                bgColorSecondary={feature.bgColorSecondary}
              >
                {feature.icon}
              </IconWrapper>
              <FeatureContent>
                <FeatureTitle level={3}>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureBlock>
          ))}
        </FeaturesGrid>

      </div>
    </WhyContainer>
  );
};

export default WhyChronoflow;
